import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, ChefHat, Truck, Home, Phone, MapPin, X, Navigation, RotateCcw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
export default function OrderStatusPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentOrder, clearCart, branding } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [restaurantSettings, setRestaurantSettings] = useState(null);

useEffect(() => {
  let tag = document.querySelector('meta[name="robots"]');

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', 'noindex, nofollow');

  return () => {
    tag.setAttribute('content', 'index, follow');
  };
}, []);

  useEffect(() => {
  if (restaurantSettings?.name) {
    document.title = `Order Status - ${restaurantSettings.name}`;
  } else {
    document.title = 'Order Status';
  }
}, [restaurantSettings]);

  // Fetch restaurant settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await api.getRestaurant();
        setRestaurantSettings(settings);
      } catch (err) {
        console.error('Failed to fetch restaurant settings:', err);
      }
    };
    fetchSettings();
  }, []);

  // Fetch order data from API
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const fetchedOrder = await api.getOrder(orderId);
        setOrder(fetchedOrder);
      } catch (err) {
        console.error('Failed to fetch order:', err);
        // Fallback to context order
        setOrder(currentOrder || {
          id: orderId,
          type: 'delivery',
          total: 0,
          items: [],
          estimatedTime: '30 - 45 mins',
          status: 'pending'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
    
    // Auto-refresh every 5 seconds to get status updates
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [orderId, currentOrder]);

  // Handle Stripe payment redirect
  useEffect(() => {
  const payment = searchParams.get('payment');

  if (payment === 'success') {
    // The browser is not trusted to mark an order as paid. The signed Stripe
    // webhook performs that transition; normal order polling observes it.
    setPaymentStatus('processing');
    window.history.replaceState({}, '', `/order-status/${orderId}`);
    clearCart();
    localStorage.removeItem('checkoutFormData');
  } 
  
  if (payment === 'cancelled') {
    setPaymentStatus('cancelled');

    // Optional but safe: clean URL
    window.history.replaceState({}, '', `/order-status/${orderId}`);
  }

  // Run once for the redirect URL only.
}, []);

  useEffect(() => {
    if (order?.paymentStatus === 'paid') {
      setPaymentStatus(current => current === 'processing' ? 'success' : current);
    }
  }, [order?.paymentStatus]);

  // Don't auto-advance - let real order status drive the UI
  useEffect(() => {
    if (!order) return;
    
    // Map order status to step index
    const statusMap = {
      'pending': 0,
      'awaiting_acceptance': 0,
      'accepted': 1,
      'preparing': 2,
      'ready': 3,
      'out_for_delivery': 3,
      'delivered': 4,
      'completed': 4
    };
    const stepIndex = statusMap[order.status] || 0;
    setCurrentStep(stepIndex);
  }, [order]);

  useEffect(() => {
    if (!order) return;
    
    const messages = {
      0: 'Hang tight! We\'ve got your order.',
      1: 'Your order has been accepted!',
      2: 'The chefs are working their magic...',
      3: order?.type === 'pickup' ? 'Your order is ready for collection!' : 'Your driver is on the way!',
      4: order?.status === 'completed' ? 'Your order is complete. Thank you!' : 'Your order has been delivered. Enjoy!'
    };
    setStatusMessage(messages[currentStep] || messages[0]);
  }, [currentStep, order]);

  // Request user location for pickup orders
  useEffect(() => {
    if (order?.type === 'pickup' && navigator.geolocation) {
      navigator.permissions?.query({ name: 'geolocation' })
        .then(result => {
          setLocationPermission(result.state);
          if (result.state === 'granted') {
            navigator.geolocation.getCurrentPosition(
              position => {
                setUserLocation({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                });
              },
              error => console.error('Location error:', error)
            );
          }
        })
        .catch(() => {
          // Fallback if permissions API not supported
          navigator.geolocation.getCurrentPosition(
            position => {
              setUserLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude
              });
              setLocationPermission('granted');
            },
            error => {
              console.error('Location error:', error);
              setLocationPermission('denied');
            }
          );
        });
    }
  }, [order]);

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationPermission('granted');
        },
        error => {
          console.error('Location error:', error);
          setLocationPermission('denied');
        }
      );
    }
  };

  const getStepState = (index) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  if (loading || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const email =
  order?.customerEmail ||
  order?.email ||
  order?.userEmail;

  const pointsEarned = (() => {
  if (!order || !restaurantSettings?.rewardsEnabled) return 0;

  const rate = restaurantSettings.pointsPerPound || 10;

  const spend = Math.max(0, (order.subtotal || 0) - (order.discount || 0));

  return Math.floor(spend * rate);
})();

  const steps = order?.type === 'pickup'
  ? [
      { key: 'awaiting_acceptance', label: 'Awaiting Acceptance', icon: Clock, description: 'Your order is placed and awaiting acceptance from the restaurant' },
      { key: 'accepted', label: 'Order Accepted', icon: CheckCircle, description: 'Your order has been accepted' },
      { key: 'preparing', label: 'Preparing', icon: ChefHat, description: 'Our chefs are freshly preparing your food' },
      {
        key: 'ready',
        label: order?.status === 'completed' ? 'Completed!' : 'Ready for Pickup!',
        icon: Home,
        description: order?.status === 'completed'
          ? 'Your order is complete. Thank you!'
          : 'Your order is ready. Please collect at the counter.'
      }
    ]
  : [
      { key: 'awaiting_acceptance', label: 'Awaiting Acceptance', icon: Clock, description: 'Your order is placed and awaiting acceptance from the restaurant' },
      { key: 'accepted', label: 'Order Accepted', icon: CheckCircle, description: 'Your order has been accepted' },
      { key: 'preparing', label: 'Preparing', icon: ChefHat, description: 'Our chefs are freshly preparing your food' },
      { key: 'out_for_delivery', label: 'On the Way', icon: Truck, description: 'Your order is out for delivery' },
      {
        key: 'delivered',
        label: order?.status === 'completed' ? 'Completed!' : 'Delivered!',
        icon: Home,
        description: order?.status === 'completed'
          ? 'Your order is complete. Thank you!'
          : 'Your order has been delivered. Enjoy!'
      }
    ];
  // Refunded orders and other terminal states do not belong in live progress.
  const isRefunded = order.paymentStatus === 'refunded';
  if (isRefunded || order.status === 'rejected' || order.status === 'expired' || order.status === 'cancelled') {
    const isExpired = order.status === 'expired';
    const isCancelled = order.status === 'cancelled' && !isRefunded;
    const TerminalIcon = isRefunded ? RotateCcw : X;
    return (
      <div className="min-h-screen bg-gray-50 font-sans">
        {/* Header */}
        <div className={`${isRefunded ? 'bg-violet-700' : isExpired ? 'bg-orange-600' : isCancelled ? 'bg-gray-600' : 'bg-red-600'} text-white`}>
          <div className="max-w-lg mx-auto px-4 py-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <TerminalIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-1">
              {isRefunded ? 'Order Refunded' : isExpired ? 'Order Expired' : isCancelled ? 'Order Cancelled' : 'Order Rejected'}
            </h1>
            <p className={`${isRefunded ? 'text-violet-200' : isExpired ? 'text-orange-200' : isCancelled ? 'text-gray-200' : 'text-red-200'} text-3xl font-bold mb-1`}>#{order.order_number || order.orderNumber}</p>
            <p className={`${isRefunded ? 'text-violet-200' : isExpired ? 'text-orange-200' : isCancelled ? 'text-gray-200' : 'text-red-200'} text-xs opacity-75`}>ID: {order.id?.slice(-8)?.toUpperCase()}</p>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
          {/* Message */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
            <p className="text-gray-800 mb-4">
              {isRefunded
                ? `Your £${Number(order.total || 0).toFixed(2)} card payment has been returned to you.`
                : isExpired 
                ? "This order expired as payment was not completed in time. Your cart items were not charged."
                : isCancelled
                ? "This order has been cancelled. No payment was processed."
                : "We're sorry, but your order has been rejected. This may be due to item availability, delivery area restrictions, or other operational reasons."}
            </p>
            <p className="text-gray-600 text-sm mb-4">
              {isRefunded
                ? "Your bank may take several working days to display the returned funds."
                : isExpired || isCancelled
                ? "Please place a new order to continue."
                : "Please contact the restaurant directly for more information or to place a new order."}
            </p>
            
            {/* Contact Information */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Contact Us</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-amber-600" />
                  <span>{restaurantSettings?.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span>{restaurantSettings?.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Order Details</h3>
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-600">{item.quantity}x {item.name}</span>
                  <span className="text-gray-900">£{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>£{Number(order.subtotal || 0).toFixed(2)}</span>
                </div>
                {Number(order.deliveryFee || 0) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Delivery fee</span>
                    <span>£{Number(order.deliveryFee || 0).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.serviceCharge || 0) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Service charge</span>
                    <span>£{Number(order.serviceCharge || 0).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.discount || 0) > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Discount</span>
                    <span>−£{Number(order.discount || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span>£{Number(order.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return (
     <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <div className="bg-amber-700 text-white">
        <div className="max-w-lg mx-auto px-4 py-6 text-center">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-3 p-3">
            {branding?.logoUrl ? (
              <img 
                src={branding.logoUrl} 
                alt="Restaurant Logo" 
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <CheckCircle className="w-12 h-12 text-amber-700" />
            )}
          </div>
          <h1 className="text-2xl font-bold mb-1">Order Placed!</h1>
          <p className="text-amber-200 text-3xl font-bold mb-1">#{order.orderNumber}</p>
          <p className="text-amber-200 text-xs opacity-75">ID: {order.id?.slice(-8)?.toUpperCase()}</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Payment Status Messages */}
        {paymentStatus === 'processing' && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-blue-800 font-semibold text-sm">Confirming your payment</p>
              <p className="text-blue-700 text-xs">This normally takes only a few seconds. You can safely leave this page open.</p>
            </div>
          </div>
        )}
        {paymentStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-green-800 font-semibold text-sm">Payment Successful!</p>
              <p className="text-green-700 text-xs">Your payment has been processed securely</p>
            </div>
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-red-800 font-semibold text-sm mb-1">Payment Cancelled</p>
            <p className="text-red-700 text-xs">You cancelled the payment. Your order is still pending.</p>
          </div>
        )}

        {/* Status Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
          <p className="text-amber-800 font-medium">{statusMessage}</p>
        </div>

        {/* Estimated Time Window */}
{order?.status !== 'pending' &&
  order?.status !== 'awaiting_acceptance' &&
  order?.status !== 'rejected' &&
  order?.status !== 'cancelled' &&
  order?.status !== 'expired' && (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-amber-600" />
        <h3 className="font-semibold text-gray-900">
          {order?.type === 'pickup'
            ? 'Estimated Pickup Time'
            : 'Estimated Delivery Time'}
        </h3>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
        {(() => {
          try {
             const formatTime = (date) =>
    date.toLocaleTimeString("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

            // IMPORTANT: backend uses scheduledFor, NOT scheduledTime
            if (
              order?.scheduledFor &&
              order.scheduledFor !== "asap" &&
              order.scheduledFor !== "ASAP"
            ) {
              return (
                <>
                  <p className="text-3xl font-bold text-gray-900 mb-1">
                    {order.scheduledFor}
                  </p>
                  <p className="text-sm text-gray-600">
                    Your order is scheduled for this time
                  </p>
                </>
              );
            }

            // ASAP fallback
           const orderTime = new Date(order.createdAt + "Z");

let leadTimeStr =
  order?.type === "delivery" ? "30-45" : "10-20";

if (restaurantSettings) {
  leadTimeStr =
    order?.type === "delivery"
      ? (restaurantSettings.deliveryTime || "30-45 mins")
          .replace(" mins", "")
          .replace("mins", "")
          .trim()
      : (restaurantSettings.pickupTime || "10-20 mins")
          .replace(" mins", "")
          .replace("mins", "")
          .trim();
}

const [minTime, maxTime] = leadTimeStr
  .split("-")
  .map((t) => parseInt(t.trim()));

// ADD minutes directly to backend UTC date
const estStart = new Date(
  orderTime.getTime() + minTime * 60000
);

const estEnd = new Date(
  orderTime.getTime() + maxTime * 60000
);

// FORMAT directly in UK timezone

return (
  <>
    <p className="text-3xl font-bold text-gray-900 mb-1">
      {formatTime(estStart)} - {formatTime(estEnd)}
    </p>
    <p className="text-sm text-gray-600">
      {order?.type === "pickup"
        ? "Please collect your order between these times"
        : "Your order will arrive between these times"}
    </p>
  </>
);
          } catch (e) {
            console.error(e);
            return (
              <p className="text-gray-600">
                Estimated time will be shown once order is accepted
              </p>
            );
          }
        })()}
      </div>
    </div>
)}

        {/* Progress Steps */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-5">Order Progress</h3>
          <div className="relative">
            {steps.map((step, index) => {
              const state = getStepState(index);
              const Icon = step.icon;
              return (
                <div key={step.key} className="flex gap-4 mb-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                      state === 'completed' ? 'bg-green-500' :
                      state === 'active' ? 'bg-amber-600 ring-4 ring-amber-200' :
                      'bg-gray-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        state === 'completed' ? 'text-white' :
                        state === 'active' ? 'text-white' :
                        'text-gray-400'
                      }`} />
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-0.5 h-10 mt-0 transition-all duration-500 ${
                        state === 'completed' ? 'bg-green-400' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                  <div className="pb-8 flex-1">
                    <p className={`font-semibold text-sm ${
                      state === 'completed' ? 'text-green-700' :
                      state === 'active' ? 'text-gray-900' :
                      'text-gray-400'
                    }`}>
                      {step.label}
                      {state === 'completed' && index === steps.length - 1 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          <span className="text-xs font-normal">Complete</span>
                        </span>
                      )}
                      {state === 'active' && index !== steps.length - 1 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                          <span className="text-xs font-normal">In progress</span>
                        </span>
                      )}
                    </p>
                    <p className={`text-xs mt-0.5 ${state === 'pending' ? 'text-gray-300' : 'text-gray-500'}`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

       
    {restaurantSettings?.rewardsEnabled && pointsEarned > 0 && (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    
    {/* TOP ROW */}
    <div className="flex items-center gap-3">
      
      {/* ICON */}
      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 relative">
  
      {/* Main emoji */}
      <span className="text-2xl animate-bounce z-10">🎉</span>

      {/* Confetti particles */}
      <span className="absolute w-1.5 h-1.5 bg-red-400 rounded-full animate-confetti1"></span>
      <span className="absolute w-1.5 h-1.5 bg-blue-400 rounded-full animate-confetti2"></span>
      <span className="absolute w-1.5 h-1.5 bg-green-400 rounded-full animate-confetti3"></span>
      <span className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full animate-confetti4"></span>

    </div>

      {/* TEXT */}
      <div className="flex-1">
        <p className="text-xl font-bold text-gray-900">
          {pointsEarned} points earned
        </p>
        {email && (
          <p className="text-xs text-gray-500 mt-1">
            Added to <span className="font-medium text-gray-700">{email}</span>
          </p>
        )}
      </div>

    </div>

    {/* EXTRA DETAIL (NOW OUTSIDE FLEX ROW) */}
    <div className="mt-3 text-sm text-gray-600 leading-relaxed">
      <p>
        You earn{" "}
        <span className="font-semibold text-gray-900">
          {restaurantSettings.pointsPerPound || 10} points per £1
        </span>{" "}
        spent.
      </p>
      <p className="mt-1">
        Your points are saved automatically and can be used for discounts on future orders.
      </p>
    </div>

  </div>
)}

 {/* Restaurant Contact */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">Restaurant Details</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span>{restaurantSettings?.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span>{restaurantSettings?.address}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors duration-200"
          >
            Back to Menu
          </button>
          <button
            onClick={() => navigate('/account')}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl font-semibold text-sm hover:bg-amber-700 transition-colors duration-200"
          >
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
}
